import React from 'react';

import { getRouteApi } from '@tanstack/react-router';
import { ALPHA_2_ISO_3166, LOAN_REQUEST_COMMANDS } from 'cpool-constants';
import { useTranslation } from 'react-i18next';

import TitleCombobox from '~/features/leadDetails/components/Overview/CustomerData/Employment/inputs/general/TitleCombobox.tsx';
import TitleComposedInput from '~/features/leadDetails/components/Overview/CustomerData/Employment/inputs/general/TitleComposedInput.tsx';
import TitleCurrencyInput from '~/features/leadDetails/components/Overview/CustomerData/Employment/inputs/general/TitleCurrencyInput.tsx';
import TitleDateInput from '~/features/leadDetails/components/Overview/CustomerData/Employment/inputs/general/TitleDateInput.tsx';
import TitleInput from '~/features/leadDetails/components/Overview/CustomerData/Employment/inputs/general/TitleInput.tsx';
import { FormCard } from '~/features/leadDetails/components/Overview/CustomerData/shared/FormCard';
import { useAclPermissions } from '~/features/leadDetails/hooks/useAclPermissions';
import { canExecuteApplicantCommand } from '~/features/leadDetails/utils/aclPermissions';

const Pensioner: React.FC<{ secondApplicant?: boolean }> = ({ secondApplicant }) => {
    const { leadId } = getRouteApi('/_app/leads_/details/$leadId').useParams();
    const { permissions } = useAclPermissions({ loanRequestId: leadId });
    const { t } = useTranslation();
    const employmentTranslationPath = 'CREDIT_WORTHINESS.SECTIONS.EMPLOYMENT';
    const incomeTranslationPath = 'CREDIT_WORTHINESS.SECTIONS.INCOME';

    const paths = {
        pensionFundCity: 'beschaeftigung.rentner.rentenversicherung.anschrift.ort',
        pensionFundHouseNumber: 'beschaeftigung.rentner.rentenversicherung.anschrift.hausnummer',
        pensionFundLand: 'beschaeftigung.rentner.rentenversicherung.anschrift.land',
        pensionFundName: 'beschaeftigung.rentner.rentenversicherung.name',
        pensionFundStreet: 'beschaeftigung.rentner.rentenversicherung.anschrift.strasse',
        pensionFundZipCode: 'beschaeftigung.rentner.rentenversicherung.anschrift.plz',
        pensionIncome: 'beschaeftigung.rentner.staatlicheRenteMonatlich',
        pensionSince: 'beschaeftigung.rentner.rentnerSeit',
    };

    const isDisabled = !canExecuteApplicantCommand({
        applicant: secondApplicant ? 2 : 1,
        commandName: LOAN_REQUEST_COMMANDS.changeApplicantEmployment,
        permissions,
    });

    return (
        <>
            <FormCard data-testid={'pensioner-sub-form'}>
                <TitleDateInput
                    title={t(`${employmentTranslationPath}.PENSIONER_SINCE`)}
                    name={paths.pensionSince}
                    disabled={isDisabled}
                />
            </FormCard>
            <FormCard title={t(`${employmentTranslationPath}.SUBSECTIONS.INCOME_HEALTH_INSURANCE`)}>
                <TitleCurrencyInput
                    title={t(`${incomeTranslationPath}.AMOUNT_OF_PENSION`)}
                    name={paths.pensionIncome}
                    disabled={isDisabled}
                />
            </FormCard>
            <FormCard title={t(`${employmentTranslationPath}.SUBSECTIONS.PENSION_FUND`)}>
                <TitleInput
                    title={t(`${employmentTranslationPath}.PENSION_FUND_NAME`)}
                    name={paths.pensionFundName}
                    placeholder={t(`${employmentTranslationPath}.PENSION_FUND_NAME`)}
                    disabled={isDisabled}
                />
                <TitleComposedInput
                    firstTitle={t('ADDRESS.STREET')}
                    secondTitle={t('ADDRESS.HOUSE_NUMBER')}
                    firstInputName={paths.pensionFundStreet}
                    secondInputName={paths.pensionFundHouseNumber}
                    firstPlaceholder={t('ADDRESS.STREET')}
                    secondPlaceholder={t('ADDRESS.HOUSE_NUMBER_SHORT')}
                    disabled={isDisabled}
                />
                <TitleComposedInput
                    firstTitle={t('ADDRESS.ZIP_CODE')}
                    secondTitle={t('ADDRESS.LOCATION')}
                    firstInputName={paths.pensionFundZipCode}
                    secondInputName={paths.pensionFundCity}
                    firstPlaceholder={t('ADDRESS.ZIP_CODE_SHORT')}
                    secondPlaceholder={t('ADDRESS.LOCATION')}
                    firstKeepDigits={5}
                    swapSize={true}
                    disabled={isDisabled}
                />
                <TitleCombobox
                    title={t('ADDRESS.COUNTRY')}
                    name={paths.pensionFundLand}
                    translationPath={'countries'}
                    values={Object.values(ALPHA_2_ISO_3166)}
                    disabled={isDisabled}
                />
            </FormCard>
        </>
    );
};

export default Pensioner;
